import {ChangeDetectionStrategy, Component, Input, SecurityContext, ViewEncapsulation} from '@angular/core';
import {DomSanitizer}                                                                  from '@angular/platform-browser';
import {ActivatedRoute}                                                                from '@angular/router';
import {Observable, of}                                                                from 'rxjs';
import {LanguageStore}                                                                 from '../stores/translation/language.store';
import {MapViewStore}                                                                  from '../stores/layout/mapview.store';
import {APP_CONFIGURATION}                                                             from '../ap-core/config';
import { ApKendoIconService } from '../icons/ap-kendo-icon.service';

@Component({
  selector: 'ap-pdf-viewer',
  templateUrl: 'ap-pdf-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})

/**
 * Pdf Viewer component which renders a pdf document as plain html.
 * This component may be used
 * (1) within another component e.g.: <ap-pdf-viewer>....
 * (2) may be used in a route to display the pdf. e.g.:
 * path: 'PdfTest',
 * component: ApPdfViewerComponent,
 * data: {
 *                relativePath: 'pathtoPdf.pdf',
 *                hideMap: true,
 *                login: false,
 *                layout: PageMode.HALFSCREEN
 *            }
 */
export class ApPdfViewerComponent {
  protected readonly ApKendoIconService = ApKendoIconService;
  @Input()
  set relativePath(value: string) {
    this.BuildPdfPathFromRelative(value);
  }

  @Input()
  set absolutePath(value: string) {
    this.BuildPdfPathFromAbsolute(value);
  }

  @Input() public showZoomButtons = false;
  @Input() public showDownloadButton = false;
  @Input() public caption: string = null;
  @Input() public loading$: Observable<boolean> = of(true);
  public secureUrl: string = null;
  public filename: string = null;
  public errorLoading = false;
  public zoom = 1;

  constructor(private languageStore: LanguageStore,
              private mapViewStore: MapViewStore,
              private domSanitizer: DomSanitizer,
              private route: ActivatedRoute) {

    const routeData = this.route.snapshot.data;
    if (routeData.caption && routeData.caption.length > 0) {
      this.caption = routeData.caption;
    }

    if (routeData.relativePath && routeData.relativePath.length > 0) {
      this.relativePath = routeData.relativePath;
    }

    if (routeData.hideMap === true) {
      this.mapViewStore.hideMapView();
    } else if (routeData.hideMap === false) {
      this.mapViewStore.showMapView();
    }

    if (routeData.showZoomButtons) {
      this.showZoomButtons = true;
    }

    if (routeData.showDownloadButton) {
      this.showDownloadButton = true;
    }
  }

  private BuildPdfPathFromRelative(relativePath: string): void {
    if (relativePath == null || relativePath.length === 0) {
      return;
    }

    if (!relativePath.startsWith('/')) {
      relativePath = '/' + relativePath;
    }
    this.BuildPdfPathFromAbsolute(APP_CONFIGURATION.BackendAddress + relativePath);
  }

  private BuildPdfPathFromAbsolute(absolutePath: string): void {
    if (absolutePath == null || absolutePath.length === 0) {
      return;
    }
    this.secureUrl = this.domSanitizer
      .sanitize(SecurityContext.RESOURCE_URL,
        this.domSanitizer.bypassSecurityTrustResourceUrl(absolutePath));

    this.filename = this.secureUrl.replace(/^.*[\\\/]/, '');
  }

  pageRendered(): void {
    this.loading$ = of(false);
  }

  onError(): void {
    this.errorLoading = true;
    this.loading$ = of(false);
  }

  zoomOut(): void {
    this.zoom = this.zoom - 0.1;
  }

  zoomIn(): void {
    this.zoom = this.zoom + 0.1;
  }
}
